import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { APP_CONSTANTS } from '@/lib/constants/app'
import { User, Building, Home, Phone, Calendar, Users } from 'lucide-react'
import type { UseFormRegister, UseFormWatch, FieldErrors } from 'react-hook-form'
import type { RegistrationFormData } from '@/lib/types/registration'

interface PersonalDetailsSectionProps {
  register: UseFormRegister<RegistrationFormData>
  watch: UseFormWatch<RegistrationFormData>
  errors: FieldErrors<RegistrationFormData>
}

export function PersonalDetailsSection({ register, watch, errors }: PersonalDetailsSectionProps) {
  const selectedBuilding = watch('building')

  return (
    <Card variant="premium">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle>Personal Details</CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">Tell us about yourself</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Full Name */}
          <div className="md:col-span-2">
            <Label htmlFor="fullName" className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-slate-400" />
              Full Name
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="fullName"
              {...register('fullName')}
              placeholder="Enter your full name"
              className={errors.fullName ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.fullName.message}
              </p>
            )}
          </div>

          {/* Building */}
          <div>
            <Label htmlFor="building" className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-slate-400" />
              Building
              <span className="text-destructive">*</span>
            </Label>
            <Select
              id="building"
              {...register('building')}
              className={errors.building ? 'border-destructive focus:border-destructive' : ''}
            >
              <option value="">Select Building</option>
              {APP_CONSTANTS.BUILDING_OPTIONS.map((building) => (
                <option key={building} value={building}>
                  {building}
                </option>
              ))}
            </Select>
            {errors.building && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.building.message}
              </p>
            )}
          </div>

          {/* Other Building */}
          {selectedBuilding === 'Other' && (
            <div>
              <Label htmlFor="otherBuilding" className="flex items-center gap-2 mb-2">
                <Building className="w-4 h-4 text-slate-400" />
                Building Name
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="otherBuilding"
                {...register('otherBuilding')}
                placeholder="Enter building name"
                className={errors.otherBuilding ? 'border-destructive focus:border-destructive' : ''}
              />
              {errors.otherBuilding && (
                <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                  {errors.otherBuilding.message}
                </p>
              )}
            </div>
          )}

          {/* Wing / Flat */}
          <div className={selectedBuilding === 'Other' ? '' : 'md:col-start-2'}>
            <Label htmlFor="wingFlat" className="flex items-center gap-2 mb-2">
              <Home className="w-4 h-4 text-slate-400" />
              Wing / Flat No
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="wingFlat"
              {...register('wingFlat')}
              placeholder="e.g., A-101"
              className={errors.wingFlat ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.wingFlat && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.wingFlat.message}
              </p>
            )}
          </div>

          {/* Mobile Number */}
          <div>
            <Label htmlFor="mobileNumber" className="flex items-center gap-2 mb-2">
              <Phone className="w-4 h-4 text-slate-400" />
              Mobile Number
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="mobileNumber"
              {...register('mobileNumber')}
              placeholder="10-digit mobile number"
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, arrows
                if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  return
                }
                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
                  return
                }
                // Allow only numbers
                if (!/^\d$/.test(e.key)) {
                  e.preventDefault()
                }
              }}
              onInput={(e) => {
                // Remove any non-numeric characters that might get pasted
                const target = e.target as HTMLInputElement
                target.value = target.value.replace(/\D/g, '')
              }}
              className={errors.mobileNumber ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.mobileNumber && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.mobileNumber.message}
              </p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <Label htmlFor="dob" className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              Date of Birth
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="dob"
              type="date"
              {...register('dob', { valueAsDate: true })}
              max={new Date().toISOString().split('T')[0]}
              className={errors.dob ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.dob && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.dob.message as string}
              </p>
            )}
          </div>

          {/* Gender */}
          <div className="md:col-span-2">
            <Label className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-slate-400" />
              Gender
              <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-3">
              {APP_CONSTANTS.GENDER_OPTIONS.map((gender) => (
                <label
                  key={gender}
                  className="relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-slate-200 cursor-pointer
                    hover:border-primary/30 hover:bg-slate-50 transition-all duration-200
                    has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                >
                  <input
                    type="radio"
                    {...register('gender')}
                    value={gender}
                    className="w-5 h-5 accent-primary cursor-pointer"
                  />
                  <span className="font-medium text-slate-700">{gender}</span>
                </label>
              ))}
            </div>
            {errors.gender && (
              <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.gender.message}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
